import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserCog, Pencil, Plus, Trash2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const req = async (url, opts = {}) => {
  const res = await fetch(`${API}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
};

const roleColor = (id) =>
  id === "super_admin" ? "from-red-500 to-rose-600" :
  id === "admin" ? "from-blue-500 to-blue-600" :
  id === "content_manager" ? "from-purple-500 to-purple-600" :
  id === "support_staff" ? "from-emerald-500 to-emerald-600" :
  id === "analyst" ? "from-amber-500 to-amber-600" :
  "from-slate-500 to-slate-700";

export default function RolesTab({ readOnly = false }) {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]); // [{group, permissions:[{value,name}]}]
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null); // role object being edited
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([req(`/admin/console/roles`), req(`/admin/console/permissions`)]);
      setRoles(r.roles || []);
      setCatalog(p.permissions || []);
    } catch (e) {
      toast.error(e.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const delRole = async (role) => {
    if (!window.confirm(`Delete custom role "${role.name}"? This cannot be undone.`)) return;
    try {
      await req(`/admin/console/roles/${role.id}`, { method: "DELETE" });
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="roles-tab-content">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Roles & Permissions</h2>
        {!readOnly && (
          <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-role-btn">
            <Plus className="h-4 w-4 mr-2" /> Create custom role
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid gap-4">
          {roles.map((role) => (
            <Card key={role.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`role-card-${role.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm bg-gradient-to-br ${roleColor(role.id)}`}>
                      <UserCog className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{role.name}</h3>
                        {role.is_system_role ? (
                          <Badge variant="secondary" className="text-xs">System</Badge>
                        ) : (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">Custom</Badge>
                        )}
                        {role.customized && role.is_system_role && (
                          <Badge variant="outline" className="text-xs text-amber-600">Customized</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{role.permission_count} permissions{role.description ? ` · ${role.description}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setEditRole(role)} data-testid={`edit-role-${role.id}`}>
                      {(role.id === "super_admin" || readOnly) ? <Lock className="h-4 w-4 mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
                      {(role.id === "super_admin" || readOnly) ? "View" : "Edit permissions"}
                    </Button>
                    {!role.is_system_role && !readOnly && (
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => delRole(role)} data-testid={`delete-role-${role.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editRole && (
        <RoleEditModal role={editRole} catalog={catalog} readOnly={readOnly} onClose={() => setEditRole(null)} onSaved={() => { setEditRole(null); load(); }} />
      )}
      {creating && (
        <RoleCreateModal catalog={catalog} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function PermissionGrid({ catalog, selected, toggle, disabled }) {
  return (
    <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
      {catalog.map((grp) => (
        <div key={grp.group}>
          <p className="text-xs font-semibold uppercase text-slate-400 mb-1">{grp.group}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {grp.permissions.map((p) => (
              <label key={p.value} className={`flex items-center gap-2 text-sm rounded px-2 py-1 ${disabled ? "opacity-60" : "hover:bg-slate-50 cursor-pointer"}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-indigo-600"
                  checked={selected.has(p.value)}
                  disabled={disabled}
                  onChange={() => toggle(p.value)}
                  data-testid={`perm-${p.value}`}
                />
                <span className="text-slate-700">{p.value}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RoleEditModal({ role, catalog, onClose, onSaved, readOnly = false }) {
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const locked = role.id === "super_admin" || readOnly;

  useEffect(() => {
    (async () => {
      try {
        const d = await req(`/admin/console/roles/${role.id}`);
        setSelected(new Set(d.role?.permissions || []));
      } catch (e) {
        toast.error(e.message || "Failed to load role");
      } finally {
        setLoading(false);
      }
    })();
  }, [role.id]);

  const toggle = (v) => setSelected((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await req(`/admin/console/roles/${role.id}/permissions`, {
        method: "PUT", body: JSON.stringify({ permissions: [...selected] }),
      });
      toast.success("Permissions updated");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="role-edit-modal">
        <DialogTitle>{locked ? `${role.name} permissions` : `Edit ${role.name} permissions`}</DialogTitle>
        <DialogDescription>
          {role.id === "super_admin"
            ? "Super Admin always has full access and cannot be restricted."
            : readOnly
            ? "Viewing the permissions for this role. Role definitions are edited in Preview and promoted to Production."
            : `Select the permissions granted to the ${role.name} role. Changes apply immediately to everyone with this role.`}
        </DialogDescription>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <PermissionGrid catalog={catalog} selected={selected} toggle={toggle} disabled={locked} />
        )}
        <div className="flex justify-between items-center pt-2">
          <span className="text-xs text-slate-500">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {!locked && (
              <Button onClick={save} disabled={saving || loading} data-testid="role-save-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoleCreateModal({ catalog, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (v) => setSelected((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const save = async () => {
    if (name.trim().length < 2) return toast.error("Enter a role name");
    setSaving(true);
    try {
      await req(`/admin/console/roles`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim(), permissions: [...selected] }),
      });
      toast.success("Custom role created");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="role-create-modal">
        <DialogTitle>Create custom role</DialogTitle>
        <DialogDescription>Define a new role and choose exactly which permissions it grants.</DialogDescription>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Role name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Finance Viewer" data-testid="role-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Description (optional)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" data-testid="role-desc-input" />
          </div>
        </div>
        <div className="pt-1">
          <label className="text-xs font-semibold text-slate-500">Permissions</label>
          <PermissionGrid catalog={catalog} selected={selected} toggle={toggle} disabled={false} />
        </div>
        <div className="flex justify-between items-center pt-2">
          <span className="text-xs text-slate-500">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="role-create-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create role
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
